<template>
  <Card class="mt-3">
    <template #title>
      <div class="flex flex-column gap-2 w-full">
        <div
          class="flex flex-column sm:flex-row sm:justify-content-between sm:align-items-start gap-2"
        >
          <div class="flex flex-column">
            <span class="text-lg font-medium">{{ title }}</span>
            <span v-if="metricLabel" class="text-xs text-500 md-metric-label">
              {{ metricLabel }}
            </span>
          </div>
          <div
            class="flex flex-column sm:flex-row gap-2 align-items-start sm:align-items-center justify-content-end"
          >
            <Dropdown
              v-if="hasPeriodControls"
              :model-value="selectedPeriod"
              :options="periodOptions"
              option-label="label"
              option-value="value"
              class="w-full sm:w-14rem"
              @update:model-value="onPeriodChange"
            />
            <Dropdown
              v-if="hasMetricControls"
              :model-value="selectedMetric"
              :options="metricOptions"
              option-label="label"
              option-value="value"
              class="w-full sm:w-14rem"
              @update:model-value="onMetricChange"
            />
          </div>
        </div>
      </div>
    </template>
    <template #content>
      <div class="md-chart-container">
        <Skeleton v-if="loading" height="260px" class="md-chart-skeleton" />
        <div v-else-if="error" class="md-empty">
          {{ error }}
        </div>
        <line-chart
          v-else-if="datasets.length"
          :data="datasets"
          :prefix="valuePrefix"
          :suffix="valueSuffix"
          :round="2"
        ></line-chart>
        <div v-else class="md-empty">
          {{ emptyMessage }}
        </div>
      </div>
    </template>
  </Card>
</template>

<script setup>
import { defineEmits, defineProps, computed } from "vue";
import Card from "primevue/card";
import Skeleton from "primevue/skeleton";
import Dropdown from "primevue/dropdown";

const emit = defineEmits(["update:metric", "update:period"]);

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  metricLabel: {
    type: String,
    default: "",
  },
  datasets: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: "",
  },
  emptyMessage: {
    type: String,
    default: "No trend data available.",
  },
  metricOptions: {
    type: Array,
    default: () => [],
  },
  selectedMetric: {
    type: [String, Number, null],
    default: null,
  },
  periodOptions: {
    type: Array,
    default: () => [],
  },
  selectedPeriod: {
    type: [String, Number, null],
    default: null,
  },
  valuePrefix: {
    type: String,
    default: "",
  },
  valueSuffix: {
    type: String,
    default: "",
  },
});

const hasMetricControls = computed(() => props.metricOptions.length > 0);
const hasPeriodControls = computed(() => props.periodOptions.length > 0);

const onMetricChange = (value) => {
  emit("update:metric", value);
};

const onPeriodChange = (value) => {
  emit("update:period", value);
};
</script>

<style scoped>
.md-chart-container {
  min-height: 260px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.md-metric-label {
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.md-empty {
  width: 100%;
  border: 1px dashed var(--surface-border);
  border-radius: 12px;
  padding: 1.25rem;
  text-align: center;
  font-size: 0.9rem;
  color: var(--text-color-secondary);
  background: var(--surface-ground);
}
</style>
